import { inject, Injectable, provideAppInitializer } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

const CHUNK_LOAD_ERROR_PATTERNS = [
  /Loading chunk/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
];
const CHUNK_RELOAD_QUERY_PARAM = '__sbe_chunk_reload';
const CHUNK_RELOAD_GUARD_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class LazyChunkRecoveryService {
  private reloadRequested = false;

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => {
        if (this.isChunkLoadError(event.error)) {
          this.reloadWithLatestBuild();
        }
      });
  }

  private isChunkLoadError(error: unknown): boolean {
    if (typeof error === 'string') {
      return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(error));
    }

    if (!error || typeof error !== 'object') return false;

    const candidate = error as { message?: unknown; name?: unknown };
    const text = [candidate.name, candidate.message]
      .filter((value): value is string => typeof value === 'string')
      .join(': ');

    return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(text));
  }

  private reloadWithLatestBuild(): void {
    if (this.reloadRequested) return;

    const now = Date.now();
    const url = new URL(window.location.href);
    const previousReloadAt = Number(url.searchParams.get(CHUNK_RELOAD_QUERY_PARAM));

    // A broken deployment must not cause an immediate reload loop.
    if (previousReloadAt > 0 && Math.abs(now - previousReloadAt) < CHUNK_RELOAD_GUARD_MS) return;

    this.reloadRequested = true;
    url.searchParams.set(CHUNK_RELOAD_QUERY_PARAM, String(now));
    this.replaceLocation(url.toString());
  }

  protected replaceLocation(url: string): void {
    window.location.replace(url);
  }
}

/**
 * Firebase Hosting SPA 적용 가이드
 *
 * - standalone 앱: ApplicationConfig.providers에 provideLazyChunkRecovery()를 추가합니다.
 * - NgModule 앱: 루트 AppComponent에서 LazyChunkRecoveryService를 주입해 초기화합니다.
 * - content-hash가 붙은 lazy chunk가 재배포로 사라지고, 새로고침 시 최신 index.html을
 *   받는 브라우저 앱에만 사용합니다. 네이티브 번들은 별도의 업데이트 정책이 필요합니다.
 *
 * TODO(sbe-web-v4 sync): sbe-main-web, sbe-gate-web, sbe-sms-web은 sbe-web-v4의 동기화
 * 결과를 우선 사용합니다. nginx에서 Firebase Hosting으로 전환한 뒤 중복 구현을 정리하고,
 * 필요한 앱에 이 가이드에 따라 적용합니다.
 */
export function provideLazyChunkRecovery() {
  return provideAppInitializer(() => {
    inject(LazyChunkRecoveryService);
  });
}
